import type { CardView } from "../api/types";
import type { Theme } from "../theme/pixel";
import { factionFor } from "../theme/factions";
import { E32 } from "../theme/endesga32";
import { panelStyle } from "../theme/styles";
import { Card, naturalCardWidthPx } from "./Card";
import { KeywordIconStrip } from "./KeywordIconStrip";
import type { PrimaryAction } from "../logic/actionFor";

interface Props {
  card: CardView | null;
  theme: Theme;
  primary: PrimaryAction | null;
  onPrimary: () => void;
  /** When true, the panel renders without its own panelStyle border —
   *  used in compact mode where preview + log share one outer panel. */
  embedded?: boolean;
}

// Reserve fixed sizes so the EventLog below doesn't reflow when the
// inspected card changes / clears.
const HERO_W = naturalCardWidthPx("hero");
// 32px icons render the 32×32 art 1:1 (crisp); the slot adds the strip's
// 4px top margin plus a little breathing room.
const KEYWORD_SLOT_H = 40;
const BUTTON_SLOT_H = 40;

export function PreviewPanel({ card, theme, primary, onPrimary, embedded = false }: Props) {
  const kws = card?.keywords ?? [];
  const sicknessIcon =
    !!card && !card.face_down && card.type === "creature" && !!card.summoning_sickness;
  const hasKeywordStrip =
    !!card && !card.face_down && card.type === "creature" && (kws.length > 0 || sicknessIcon);

  return (
    <div
      // Marker for the click-outside-cards hook — without this, clicking
      // the primary button clears the inspect state before its handler
      // runs.
      data-keep-focus="true"
      style={{
        ...(embedded ? {} : panelStyle(theme)),
        padding: 14,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        minHeight: 0,
        flex: "1 1 auto",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          // Bottom-anchor the card so the keyword strip below always sits
          // the same fixed distance beneath it. Centering instead let the
          // gap grow with panel height (strip drifting far from the card)
          // or, when the strip wrapped, crowd the card's lower edge. The
          // empty-state placeholder has nothing below it, so keep it
          // centered.
          alignItems: card ? "flex-end" : "center",
          width: "100%",
        }}
      >
        {card ? (
          <div
            style={{
              maxWidth: HERO_W,
              maxHeight: "100%",
              aspectRatio: "5 / 7",
              width: "100%",
            }}
          >
            <Card
              card={card}
              size="hero"
              theme={theme}
              keywordTooltips
              faceDown={card.face_down}
            />
          </div>
        ) : (
          <div
            style={{
              maxWidth: HERO_W,
              maxHeight: "100%",
              width: "100%",
              aspectRatio: "5 / 7",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              color: theme.inkFaint,
              textAlign: "center",
              border: `2px dashed ${theme.panelEdge}`,
              padding: "40px 20px",
              boxSizing: "border-box",
            }}
          >
            <div style={{ fontSize: 48, opacity: 0.4, fontFamily: theme.fontDisplay }}>
              {theme.ornament}
            </div>
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 12,
                lineHeight: 1.6,
              }}
            >
              Click a card
              <br />
              to inspect it here.
            </div>
          </div>
        )}
      </div>

      <div style={{ height: KEYWORD_SLOT_H, display: "flex", justifyContent: "center" }}>
        {hasKeywordStrip && (
          <div style={{ maxWidth: HERO_W }}>
            <KeywordIconStrip
              keywords={kws}
              theme={theme}
              size={32}
              summoningSickness={sicknessIcon}
            />
          </div>
        )}
      </div>

      <div style={{ height: BUTTON_SLOT_H }}>
        {card && primary ? (
          <PrimaryButton primary={primary} card={card} theme={theme} onClick={onPrimary} />
        ) : null}
      </div>
    </div>
  );
}

function PrimaryButton({
  primary,
  card,
  theme,
  onClick,
}: {
  primary: PrimaryAction;
  card: CardView;
  theme: Theme;
  onClick: () => void;
}) {
  const f = factionFor(card.faction);
  const enabled = !primary.disabled;
  const accent = primary.emphasis === "confirm" ? theme.accent : f.color;
  return (
    <button
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      className="doi-btn"
      style={{
        padding: "10px 14px",
        background: enabled ? accent : theme.bgDeep,
        color: enabled ? (primary.emphasis === "confirm" ? E32.white : f.ink) : theme.inkFaint,
        border: `2px solid ${enabled ? accent : theme.panelEdge}`,
        borderRadius: theme.radius.button,
        fontFamily: theme.fontDisplay,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: theme.letterSpacing,
        textTransform: theme.uppercase ? "uppercase" : "none",
        cursor: enabled ? "pointer" : "not-allowed",
        imageRendering: "pixelated",
        transition: "none",
      }}
    >
      {primary.label}
    </button>
  );
}
